import { isAncestorFolder } from '../../../../utils/docOrganization';
import { isAtlasUnfiledId } from '../constants';

/**
 * Left / right / nest zone for an Atlas folder under the pointer.
 * Same-level siblings use a midpoint so a horizontal drag reorders.
 * Other folders keep a middle nest band with side “insert as sibling” gutters.
 */
export function atlasSideZone(clientX, targetRect, { sibling = false } = {}) {
  if (!targetRect || !(targetRect.width > 0)) return sibling ? 'after' : 'middle';
  const x = clientX - targetRect.left;
  if (sibling) return x < targetRect.width / 2 ? 'before' : 'after';
  const edge = targetRect.width * 0.28;
  if (x < edge) return 'before';
  if (x > targetRect.width - edge) return 'after';
  return 'middle';
}

/**
 * Insert a dragged folder among its siblings from a world-X drop,
 * regardless of what is under the pointer.
 *
 * @returns {{ folderId: string, zone: 'before'|'after' } | null}
 */
export function resolveAtlasSiblingOrder({
  folders = [],
  nodes = [],
  draggedIds = [],
  dropX,
}) {
  const dragged = [...new Set((draggedIds || []).filter(Boolean))];
  const primary = dragged[0];
  if (!primary || dropX == null || Number.isNaN(dropX)) return null;

  const mover = folders.find((f) => f.id === primary);
  if (!mover || isAtlasUnfiledId(primary)) return null;

  const parent = mover.parentId || null;
  const others = folders.filter(
    (f) =>
      (f.parentId || null) === parent &&
      !isAtlasUnfiledId(f.id) &&
      !dragged.includes(f.id)
  );
  if (others.length === 0) return null;

  const byId = new Map((nodes || []).map((n) => [n.id, n]));
  others.sort((a, b) => (byId.get(a.id)?.x ?? 0) - (byId.get(b.id)?.x ?? 0));

  const before = others.find((s) => {
    const n = byId.get(s.id);
    if (!n) return false;
    return dropX < n.x + (n.width || 0) / 2;
  });
  if (before) return { folderId: before.id, zone: 'before' };
  return { folderId: others[others.length - 1].id, zone: 'after' };
}

/**
 * @returns {{ folderId: string, zone: 'before'|'after'|'middle' } | null}
 */
export function resolveAtlasFolderDrop({
  folders = [],
  draggedIds = [],
  targetId,
  clientX,
  targetRect,
}) {
  if (!targetId || isAtlasUnfiledId(targetId)) return null;

  const dragged = [...new Set((draggedIds || []).filter(Boolean))];
  if (dragged.length === 1 && dragged[0] === targetId) return null;

  const target = folders.find((f) => f.id === targetId);
  if (!target) return null;

  const primary = dragged.find((id) => id !== targetId) || null;
  const mover = primary ? folders.find((f) => f.id === primary) : null;
  const sameParent =
    Boolean(mover) && (mover.parentId || null) === (target.parentId || null);

  if (primary && isAncestorFolder(folders, targetId, primary) && primary !== targetId) {
    return null;
  }

  const zone = atlasSideZone(clientX, targetRect, { sibling: sameParent });
  if (zone === 'middle') {
    return { folderId: targetId, zone: 'middle' };
  }

  const parent = target.parentId || null;
  if (primary && parent === primary) return null;
  if (primary && parent && isAncestorFolder(folders, parent, primary)) return null;
  return { folderId: targetId, zone };
}
